import { prop } from 'ramda';
import { useCallback } from 'react';
import { useTranslations } from 'js/hooks/i18n';
import { useFormatter } from 'js/hooks/useFormatter';
import { useUserSelector } from 'js/store/hooks';
import {
    useInboxSubscribe,
    useInboxSendMessage,
    INBOX_SUBSCRIBE_STATUS_CODE,
    INBOX_SEND_MESSAGE_STATUS_CODE
} from 'server';
import { TFormField } from 'js/components/common/Form/types';
import { useShowNotification } from 'js/hooks/useShowNotification';

import { useGetMessageHtml } from './useGetMessageHtml';

export const useOnSubmit = ({
    tkPrefix,
    daydepositlimit,
    weekdepositlimit,
    monthdepositlimit,
    onSuccess,
    closePopup
}: {
    tkPrefix?: string;
    onSuccess: () => Promise<void>;
    closePopup: () => void;
    daydepositlimit: string;
    weekdepositlimit: string;
    monthdepositlimit: string;
}) => {
    const translate = useTranslations(tkPrefix);
    const showNotification = useShowNotification();
    const inboxSubscribe = useInboxSubscribe();
    const inboxSendMessage = useInboxSendMessage();
    const userName = useUserSelector(prop('userName'));
    const formatter = useFormatter();
    const getMessage = useGetMessageHtml({
        tkPrefix,
        daydepositlimit,
        weekdepositlimit,
        monthdepositlimit
    });

    return useCallback(async ({
        dataMap,
        fields
    }: {
        dataMap: Map<string, string>;
        fields: TFormField[];
    }) => {
        try {
            const { status: { code: subscribeStatusCode } } = await inboxSubscribe();

            if (subscribeStatusCode !== INBOX_SUBSCRIBE_STATUS_CODE.OK) {
                throw subscribeStatusCode;
            }

            const { status: { code: sendMessageStatusCode } } = await inboxSendMessage({
                category: 'SUPPORT',
                subcategory: 'ACCOUNT',
                subject: translate('subject', [userName ?? '', formatter.getDateTimeShort(Date.now())]),
                body: getMessage({
                    fields,
                    dataMap
                })
            });

            if (sendMessageStatusCode !== INBOX_SEND_MESSAGE_STATUS_CODE.OK) {
                throw sendMessageStatusCode;
            }

            closePopup();

            await onSuccess();

            showNotification(translate('success'), { variant: 'success' });

            // return true;
        } catch (error) {
            showNotification(translate('error'), { variant: 'error' });

            // return false;
        }
    }, [
        userName,
        translate,
        formatter,
        closePopup,
        getMessage,
        onSuccess,
        showNotification,
        inboxSendMessage,
        inboxSubscribe
    ]);
};
